import { useEffect, useState, useRef } from "react";
import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/Layout/PageTitle";
import TableComponent from "../../components/TableComponent";
import { SettingsListDTO } from "../../models/Settings/SettingsListDTO";
import { SortingRule } from "react-table";
import { Utilities } from "../../helpers/Utilities";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { SettingsService } from "../../services/SettingsService";
import React from "react";
import Toast from "../../helpers/Toast";
import { SettingsV2NavBar } from "../../components/Settings/SettingsV2NavBar";
import { Parameter } from "../../models/Parameter";
import ButtonComponent from "../../components/ButtonComponent";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import InputComponent from "../../components/InputComponent";
import { Row } from "reactstrap";
import SettingAuditLog from "../../components/Settings/SettingAuditLog";

function SettingsIndexV2() {

    const intl = useIntl();
    const pageSizes: number[] = [5, 10, 15, 30];
    const [data, setData] = useState<SettingsListDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(1);
    const navigate = useNavigate();
    const service = new SettingsService();

    const [currentPage, setCurrentPage] = useState<number>();
    const [currentPageSize, setCurrentPageSize] = useState<number>();
    const [currentSorting, setCurrentSorting] = useState<any[]>([]);
    const [reloadSearch, setReloadSearch] = useState<boolean>(false);
    const [isButtonCreateLoading, setIsButtonCreateLoading] = useState<boolean>(false);

    const [goToPage, setGoToPage] = useState<number>();

    const [initialSortBy] = useState<any[]>([]);
    const [page] = useState<number>(1);
    const [pageSize] = useState<number>(10);

    const [type, setType] = useState<string>("");

    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
    const [selectedSetting, setSelectedSetting] = useState<SettingsListDTO | null>(null);
    const [newValue, setNewValue] = useState<string>("");

    const [isHistoricModalOpen, setIsHistoricModalOpen] = useState<boolean>(false);

    const [showSensitiveValue, setShowSensitiveValue] = useState<boolean>(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Popup de editar setting
    const handleOpenEditModal = (setting: SettingsListDTO) => {
        setSelectedSetting(setting);
        setNewValue("");
        setShowSensitiveValue(false);
        setIsEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setSelectedSetting(null);
        setIsEditModalOpen(false);
    };

    const handleSaveChanges = async () => {
        if (selectedSetting) {

            const data = {
                id: selectedSetting.id,
                value: newValue,
            };

            setLoading(true);
            const response = await service.Edit(data);
            setLoading(false);

            if (response.success !== true) {
                Toast.Show("error", response.message);
                return;
            }

            Toast.Show("success", "Setting editada com sucesso.");
            handleCloseEditModal();

            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
        }
    };

    const handleExportSettings = async () => {
        var response = await service.ExportSettings();

        if (response.success !== true) {
            Toast.Show("error", response.message);
            return;
        }

        Toast.Show("success", "Exportação realizada com sucesso. O download será iniciado.");
        if (response.obj) {
            const blob = new Blob([response.obj], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "settings.csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }



    }

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = ""; // Reset so user can re-import the same file
            fileInputRef.current.click();
        }
    };

    const handleImportSettings = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const text = e.target?.result as string;
                if (text) {
                    try {
                        const response = await service.ImportSettings(text);
                        if (response.success) {
                            Toast.Show("success", "Importação realizada com sucesso.");
                            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
                        } else {
                            Toast.Show("error", response.message || "Erro ao importar settings.");
                        }
                    } catch (err) {
                        Toast.Show("error", "Erro ao importar settings.");
                    }
                } else {
                    Toast.Show("error", "O arquivo está vazio ou não pôde ser lido.");
                }
            };
            reader.readAsText(file);
        }
    };

    // Popup de ver histórico
    const handleOpenHistoricModal = (setting: SettingsListDTO) => {
        setSelectedSetting(setting);
        setIsHistoricModalOpen(true);
    };

    const handleCloseHistoricModal = () => {
        setSelectedSetting(null);
        setIsHistoricModalOpen(false);
    };

    const columns = React.useMemo(
        () => [
            {
                Header: "Nome",
                accessor: "name",
                width: 33,
            },

            {
                Header: "Valor",
                accessor: "value",
                Cell: ({ row }: { row: any }) => (
                    <span>
                        {row.original.isSensitive ? "******" : row.original.value}
                    </span>
                ),
                width: 33,
            },

            {
                Header: "Descrição",
                accessor: "description",
                width: 33,
            },

            {
                Header: "",
                accessor: " ",
                Cell: ({ row }: { row: any }) => (
                    <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                        <ButtonComponent text="Alterar" onClick={() => handleOpenEditModal(row.original)} />
                        <ButtonComponent text="Ver histórico" onClick={() => handleOpenHistoricModal(row.original)} />
                    </div>
                ),
            },
        ],
        [isButtonCreateLoading],
    );

    const fetchData = async (page: number, pageSize: number, sortBy: SortingRule<object>[] | null) => {
        var searchUrl: string = Utilities.CreateSearchURL([
            `page=${page}`,
            `pageSize=${pageSize}`,
            Utilities.AddParamToQueryIfValue("type", "string", type),
        ]);

        if (sortBy != null && sortBy.length > 0) {
            searchUrl += `${Utilities.AddParamToQueryIfValue("sortField", "string", sortBy[0].id)}${Utilities.AddParamToQueryIfValue(
                "sortOrder", "string", sortBy[0].desc === true ? "DESC" : "ASC",
            )}`;
        }

        navigate({
            pathname: "/SettingsV2",
            search: searchUrl,
        });

        setLoading(true);

        var response = await service.GetSettingsByType(
            mountSearchParameters(),
            Utilities.MountSortParameters(sortBy),
            page,
            pageSize,
        );

        if (response.success !== true) {
            setCurrentPage(1);
            setGoToPage(1);
            setData([]);
            setPageCount(0);
            setLoading(false);
            setCurrentSorting([]);
            Toast.Show("error", response.message);
            return;
        }

        if (response.items.length === 0) {
            setCurrentPage(1);
            setGoToPage(1);
            Toast.Show("error", "Nenhum resultado encontrado.");
        }

        setData(response.items);
        setPageCount(response.totalPages);
        setLoading(false);
        setCurrentPageSize(pageSize);
        setCurrentPage(page);
        setCurrentSorting(sortBy ?? []);
        setGoToPage(0);

        if (response.totalPages > 0 && page > response.totalPages) {
            setGoToPage(1);
        }
    };

    const mountSearchParameters = (): Parameter[] => {
        var parameter: Parameter[] = [];
        parameter.push(new Parameter("type", type));
        return parameter;
    }

    useEffect(() => {
        if (type) {
            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
        }
    }, [type]);

    const handleTabChange = (tabName: string) => {
        setType(tabName);
    }

    return (
        <Layout>
            <div className="pageContainer">
                <div className="pageHeader">
                    <PageTitle title={'Settings V2'} allowGoBack={false} />
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16, gap: 8 }}>
                    <ButtonComponent
                        text="Exportar settings para csv"
                        onClick={handleExportSettings}
                        loading={loading}
                    />
                    <ButtonComponent
                        text="Importar settings de csv"
                        onClick={handleImportButtonClick}
                        loading={false}
                    />
                    <input
                        type="file"
                        accept=".csv"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleImportSettings}
                    />
                </div>
                <div className="pageBody">
                    <SettingsV2NavBar onTabChange={handleTabChange} />
                    <div className="pageComponent" style={{ width: "100%" }}>
                        <TableComponent
                            goToPage={goToPage}
                            loading={loading}
                            pageCount={pageCount}
                            columns={columns}
                            manualPagination={true}
                            fetchData={fetchData}
                            pageSizes={pageSizes}
                            initialPage={page}
                            initialPageSize={pageSize}
                            initialSortBy={initialSortBy}
                            data={data}
                            dontOrderLastCollumn={true}
                        />
                    </div>
                </div>

            </div>

            <Dialog open={isEditModalOpen} onClose={handleCloseEditModal}>
                <DialogTitle>Alterar Setting</DialogTitle>
                <DialogContent>
                    <Row style={{ margin: "0px", padding: "0px" }}>
                        <InputComponent
                            xl={6}
                            lg={6}
                            md={6}
                            sm={12}
                            xs={12}
                            style={{ width: "100%" }}
                            type={"text"}
                            value={selectedSetting?.name ?? ""}
                            label="Nome"
                            readOnly={true}
                        />
                        <InputComponent
                            xl={6}
                            lg={6}
                            md={6}
                            sm={12}
                            xs={12}
                            style={{ width: "100%" }}
                            type={"text"}
                            value={selectedSetting?.description ?? ""}
                            label="Descrição"
                            readOnly={true}
                        />
                        <InputComponent
                            xl={6}
                            lg={6}
                            md={6}
                            sm={12}
                            xs={12}
                            style={{ width: "100%" }}
                            type={selectedSetting?.isSensitive && !showSensitiveValue ? "password" : "text"}
                            value={selectedSetting?.value ?? ""}
                            label="Valor Atual"
                            readOnly={true}
                            showIcon={selectedSetting?.isSensitive} // Exibe o ícone apenas se for sensível
                            functionIcon={() => setShowSensitiveValue(!showSensitiveValue)} // Alterna a visibilidade
                        />
                        <InputComponent
                            xl={6}
                            lg={6}
                            md={6}
                            sm={12}
                            xs={12}
                            style={{ width: "100%" }}
                            type={"text"}
                            value={newValue}
                            label="Novo Valor"
                            readOnly={loading}
                            onChange={(value: string) =>
                                setNewValue(value)
                            }
                        />
                    </Row>
                </DialogContent>
                <DialogActions>
                    <ButtonComponent text="Cancelar" onClick={handleCloseEditModal} />
                    <ButtonComponent
                        text="Alterar"
                        onClick={handleSaveChanges}
                        loading={loading}
                    />
                </DialogActions>
            </Dialog>

            <Dialog open={isHistoricModalOpen} onClose={handleCloseHistoricModal}>
                <DialogTitle>Histórico de Alterações</DialogTitle>
                <DialogContent>
                    <SettingAuditLog
                        settingId={selectedSetting?.id ?? null}
                    />
                </DialogContent>
                <DialogActions>
                    <ButtonComponent text="Cancelar" onClick={handleCloseHistoricModal} />
                </DialogActions>
            </Dialog>
        </Layout>
    );
}

export default SettingsIndexV2